import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowLeft, X } from 'lucide-react';
import { useAuth } from '../../../context/useAuth';
import SpiritualTodayWidget from './SpiritualTodayWidget';
import {
  fetchSpiritualFeed,
  spiritualWidgetsEnabled,
  canShowSpiritualForUser,
  canConfigureSpiritualContent,
  fetchCoachPinnedMedia,
  saveCoachPinnedMedia,
  fetchSpiritualExperiences,
  postSpiritualExperience,
} from '../../../utils/spiritualApi';
import CoachTodaySchedule from './CoachTodaySchedule';
import SpiritualMeetingsPanel from './SpiritualMeetingsPanel';
import { looksLikeVideoUrl, resolveSpiritualPlayer } from '../../../utils/spiritualVideoEmbed';
import { splitSpiritualBodyLinks } from '../../../utils/spiritualLinkify';
import SpiritualBibleHowTo from './SpiritualBibleHowTo';
import './SpiritualHubView.css';

function SpiritualVideoEmbed({ media, title }) {
  const player = resolveSpiritualPlayer(media);
  if (media && media.active === false) return null;
  if (!player.src) return null;
  const label = title || media?.title || 'Video';
  return (
    <div className="spiritual-hub__player-wrap">
      {player.type === 'video' ? (
        <video src={player.src} controls playsInline>
          {label}
        </video>
      ) : (
        <iframe
          title={looksLikeVideoUrl(label) ? 'Video' : label}
          src={player.src}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
          allowFullScreen
        />
      )}
    </div>
  );
}

function SpiritualBodyWithLinks({ text, className = '' }) {
  const parts = splitSpiritualBodyLinks(text);
  return (
    <div className={className}>
      {parts.map((part, i) => (
        part.type === 'link' ? (
          <a
            key={`l-${i}`}
            className="spiritual-hub__body-link"
            href={part.href}
            target="_blank"
            rel="noopener noreferrer"
          >
            {part.label}
          </a>
        ) : (
          <span key={`t-${i}`}>{part.value}</span>
        )
      ))}
    </div>
  );
}

function displayMediaTitle(media) {
  const title = String(media?.title || '').trim();
  if (!title || looksLikeVideoUrl(title)) return '';
  return title;
}

function experiencePinText(pin) {
  if (!pin) return '';
  const body = String(pin.body_text || '').trim();
  const url = String(pin.media_url || '').trim();
  if (url && body.includes(url)) return body;
  if (url && body) return `${body}\n${url}`;
  return body || url;
}

function CoachPinnedForm({ kind, label, initial, onSaved }) {
  const [url, setUrl] = useState(initial?.media_url || '');
  const [title, setTitle] = useState(initial?.title || '');
  const [bodyText, setBodyText] = useState(initial?.body_text || '');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const isPrayer = kind === 'prayer';
  const isExperience = kind === 'experience';
  const usesBody = isPrayer || isExperience;

  useEffect(() => {
    setUrl(initial?.media_url || '');
    setTitle(initial?.title || '');
    setBodyText(initial?.body_text || '');
  }, [initial?.media_url, initial?.title, initial?.body_text]);

  const save = async (active) => {
    setBusy(true);
    setMsg('');
    try {
      const row = await saveCoachPinnedMedia(kind, {
        media_url: url,
        title,
        body_text: usesBody ? bodyText : '',
        active,
      });
      onSaved?.(kind, row);
      setMsg(active ? 'Guardado' : 'Ocultado');
    } catch (e) {
      setMsg(e?.response?.data?.error || e.message || 'No se pudo guardar');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="spiritual-hub__card space-y-2">
      <p className="spiritual-today-widget__label">{label}</p>
      <input
        className="spiritual-hub__input"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder={isExperience ? 'Título (opcional, p. ej. Aviso de la semana)' : 'Título (opcional)'}
      />
      <input
        className="spiritual-hub__input"
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        placeholder={isExperience
          ? 'Enlace anclado https://… (WhatsApp, Zoom, Drive, página…)'
          : 'https://youtube.com/… o Vimeo o .mp4 (el usuario lo verá dentro de la app)'}
      />
      {usesBody ? (
        <textarea
          className="spiritual-hub__input spiritual-hub__textarea"
          rows={isExperience ? 6 : 8}
          maxLength={8000}
          value={bodyText}
          onChange={(e) => setBodyText(e.target.value)}
          placeholder={isExperience
            ? 'Mensaje opcional. El enlace de arriba lo ven todos tus alumnos.'
            : 'Texto de la oración (lo verá el usuario debajo del video)'}
        />
      ) : null}
      {isExperience && (url || splitSpiritualBodyLinks(bodyText).some((p) => p.type === 'link')) ? (
        <SpiritualBodyWithLinks
          text={experiencePinText({ body_text: bodyText, media_url: url })}
          className="spiritual-hub__prayer-text spiritual-hub__pinned-preview"
        />
      ) : null}
      <div className="flex flex-wrap gap-2">
        <button type="button" className="btn-primary text-sm" disabled={busy} onClick={() => save(true)}>
          Publicar
        </button>
        <button type="button" className="btn-secondary text-sm" disabled={busy} onClick={() => save(false)}>
          Ocultar
        </button>
      </div>
      {msg ? <p className="text-sm text-stone-300">{msg}</p> : null}
    </div>
  );
}

export default function SpiritualHubView({ onBack, onOpenBible }) {
  const { user } = useAuth();
  const hasAccess = canShowSpiritualForUser(user);
  const canConfig = canConfigureSpiritualContent(user);
  const [status, setStatus] = useState('loading');
  const [tab, setTab] = useState('hoy');
  const [feed, setFeed] = useState(null);
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [coachMedia, setCoachMedia] = useState(null);
  const [wall, setWall] = useState([]);
  const [wallDraft, setWallDraft] = useState('');
  const [wallMsg, setWallMsg] = useState('');

  const loadFeed = useCallback(async () => {
    const data = await fetchSpiritualFeed();
    setFeed(data);
    return data;
  }, []);

  useEffect(() => {
    if (!spiritualWidgetsEnabled() || !hasAccess) {
      setStatus('unavailable');
      return undefined;
    }
    let active = true;
    (async () => {
      try {
        const data = await loadFeed();
        if (!active) return;
        if (data?.enabled === false) {
          setStatus('unavailable');
          return;
        }
        setWall(Array.isArray(data?.experiences) ? data.experiences : []);
        setStatus('ready');
      } catch (err) {
        if (!active) return;
        if (err?.response?.status === 403) {
          setStatus('unavailable');
          return;
        }
        setStatus('ready');
      }
    })();
    return () => { active = false; };
  }, [hasAccess, loadFeed]);

  useEffect(() => {
    if (!canConfig || tab !== 'config') return undefined;
    let active = true;
    fetchCoachPinnedMedia()
      .then((data) => { if (active) setCoachMedia(data); })
      .catch(() => {});
    return () => { active = false; };
  }, [canConfig, tab]);

  const closeWelcome = () => setWelcomeOpen(false);

  useEffect(() => {
    if (!welcomeOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setWelcomeOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [welcomeOpen]);

  const submitWall = async () => {
    setWallMsg('');
    try {
      const row = await postSpiritualExperience(wallDraft);
      setWall((prev) => [row, ...prev]);
      setWallDraft('');
      const fresh = await fetchSpiritualExperiences();
      if (Array.isArray(fresh)) setWall(fresh);
    } catch (e) {
      setWallMsg(e?.response?.data?.error || 'No se pudo publicar');
    }
  };

  const welcome = feed?.pinned?.welcome;
  const prayer = feed?.pinned?.prayer;
  const experiencePin = feed?.pinned?.experience;
  const experiencePinBody = experiencePinText(experiencePin);

  return (
    <div className="dashboard-main-view space-y-4">
      <button type="button" className="btn-secondary text-sm inline-flex items-center gap-2" onClick={onBack}>
        <ArrowLeft className="h-4 w-4" aria-hidden />
        Inicio
      </button>
      {status === 'unavailable' ? (
        <div className="card p-8 text-center space-y-2">
          <h2 className="text-lg font-bold text-stone-900">Funcionalidad no disponible</h2>
          <p className="text-sm text-stone-600">
            El módulo Viento Recio no está habilitado para tu cuenta.
          </p>
        </div>
      ) : status === 'loading' ? (
        <p className="text-sm text-stone-600">Cargando…</p>
      ) : (
        <>
          <div className="spiritual-hub__tabs">
            <button type="button" className={tab === 'hoy' ? 'is-active' : ''} onClick={() => setTab('hoy')}>Hoy</button>
            <button type="button" className={tab === 'biblia' ? 'is-active' : ''} onClick={() => setTab('biblia')}>Biblia</button>
            <button type="button" className={tab === 'oracion' ? 'is-active' : ''} onClick={() => setTab('oracion')}>Oración de fe</button>
            <button type="button" className={tab === 'muro' ? 'is-active' : ''} onClick={() => setTab('muro')}>Experiencias Viento Recio</button>
            <button type="button" className={tab === 'reuniones' ? 'is-active' : ''} onClick={() => setTab('reuniones')}>Reuniones Viento Recio</button>
            {canConfig ? (
              <button type="button" className={tab === 'config' ? 'is-active' : ''} onClick={() => setTab('config')}>Configurar</button>
            ) : null}
          </div>

          {tab === 'hoy' ? (
            <div className="space-y-4">
              {welcome?.active !== false && resolveSpiritualPlayer(welcome).src ? (
                <section className="spiritual-today-widget">
                  <div className="spiritual-today-widget__card">
                    <p className="spiritual-today-widget__label">Bienvenida</p>
                    <p className="spiritual-today-widget__heading">
                      {displayMediaTitle(welcome) || 'Video de bienvenida'}
                    </p>
                    <p className="spiritual-today-widget__body">
                      Un mensaje de tu entrenador para empezar.
                    </p>
                    <div className="spiritual-today-widget__actions">
                      <button
                        type="button"
                        className="btn-primary text-sm"
                        onClick={() => setWelcomeOpen(true)}
                      >
                        Ver video de bienvenida
                      </button>
                    </div>
                  </div>
                </section>
              ) : null}
              <SpiritualTodayWidget onOpenBible={onOpenBible} />
            </div>
          ) : null}

          {tab === 'biblia' ? (
            <section className="spiritual-today-widget space-y-3">
              <h3 className="spiritual-today-widget__title">Biblia</h3>
              <p className="spiritual-today-widget__body">
                Lectura RVR1960: busca, abre capítulos y marca favoritos.
              </p>
              <div className="flex flex-wrap gap-2">
                {onOpenBible ? (
                  <button type="button" className="btn-primary text-sm" onClick={onOpenBible}>
                    Empezar a leer la Biblia
                  </button>
                ) : (
                  <p className="spiritual-today-widget__body">La lectura no está disponible en esta vista.</p>
                )}
              </div>
              <p className="spiritual-today-widget__body">
                Antes de leer, abre la guía: por dónde empezar y el método OIA.
              </p>
              <SpiritualBibleHowTo />
            </section>
          ) : null}

          {tab === 'oracion' ? (
            <section className="spiritual-today-widget">
              <h3 className="spiritual-today-widget__title">Oración de fe</h3>
              {prayer?.active && (resolveSpiritualPlayer(prayer).src || prayer.body_text) ? (
                <>
                  {displayMediaTitle(prayer) ? (
                    <p className="spiritual-today-widget__heading">{displayMediaTitle(prayer)}</p>
                  ) : null}
                  <SpiritualVideoEmbed media={prayer} title={displayMediaTitle(prayer) || 'Oración de fe'} />
                  {prayer.body_text ? (
                    <div className="spiritual-hub__prayer-text">
                      {prayer.body_text}
                    </div>
                  ) : null}
                </>
              ) : (
                <p className="spiritual-today-widget__body">
                  Tu entrenador aún no publicó la oración de fe.
                </p>
              )}
            </section>
          ) : null}

          {tab === 'muro' ? (
            <section className="spiritual-today-widget space-y-3">
              <h3 className="spiritual-today-widget__title">Experiencias Viento Recio</h3>
              <p className="spiritual-today-widget__body">
                Solo ves y publicas con compañeros del mismo entrenador.
              </p>
              {experiencePin?.active && experiencePinBody ? (
                <div className="spiritual-hub__card spiritual-hub__pinned">
                  <p className="spiritual-today-widget__label">Anclado por tu entrenador</p>
                  {displayMediaTitle(experiencePin) ? (
                    <p className="spiritual-today-widget__heading">{displayMediaTitle(experiencePin)}</p>
                  ) : null}
                  <SpiritualBodyWithLinks
                    text={experiencePinBody}
                    className="spiritual-hub__prayer-text spiritual-hub__pinned-body"
                  />
                </div>
              ) : null}
              <textarea
                className="spiritual-hub__input spiritual-hub__textarea"
                rows={3}
                maxLength={2000}
                value={wallDraft}
                onChange={(e) => setWallDraft(e.target.value)}
                placeholder="Comparte tu experiencia…"
              />
              <button type="button" className="btn-primary text-sm" onClick={submitWall}>Publicar</button>
              {wallMsg ? <p className="spiritual-today-widget__error">{wallMsg}</p> : null}
              <ul className="space-y-2">
                {wall.map((post) => (
                  <li key={post.id} className="spiritual-hub__card">
                    <p className="spiritual-today-widget__heading">{post.author_name}</p>
                    <p className="spiritual-today-widget__body">{post.body}</p>
                  </li>
                ))}
              </ul>
              {!wall.length ? (
                <p className="spiritual-today-widget__body">Aún no hay publicaciones de tu comunidad.</p>
              ) : null}
            </section>
          ) : null}

          {tab === 'reuniones' ? (
            <SpiritualMeetingsPanel user={user} />
          ) : null}

          {tab === 'config' && canConfig ? (
            <>
              <CoachTodaySchedule />
            <section className="spiritual-today-widget space-y-3">
              <h3 className="spiritual-today-widget__title">Videos de tu comunidad</h3>
              <p className="spiritual-today-widget__body">
                Tú configuras el contenido. Super admin solo define si tu ficha tiene Viento Recio.
              </p>
              <CoachPinnedForm
                kind="welcome"
                label="Video de bienvenida"
                initial={coachMedia?.welcome}
                onSaved={() => {
                  loadFeed();
                  fetchCoachPinnedMedia().then(setCoachMedia).catch(() => {});
                }}
              />
              <CoachPinnedForm
                kind="prayer"
                label="Oración de fe"
                initial={coachMedia?.prayer}
                onSaved={() => {
                  loadFeed();
                  fetchCoachPinnedMedia().then(setCoachMedia).catch(() => {});
                }}
              />
              <CoachPinnedForm
                kind="experience"
                label="Mensaje anclado en Experiencias"
                initial={coachMedia?.experience}
                onSaved={() => {
                  loadFeed();
                  fetchCoachPinnedMedia().then(setCoachMedia).catch(() => {});
                }}
              />
            </section>
            </>
          ) : null}
        </>
      )}

      {welcomeOpen && resolveSpiritualPlayer(welcome).src
        ? createPortal(
          <div className="spiritual-hub-modal" role="dialog" aria-modal="true" aria-labelledby="vr-welcome-title">
            <div className="spiritual-hub-modal__panel">
              <div className="flex items-start justify-between gap-3">
                <h2 id="vr-welcome-title" className="spiritual-today-widget__title">
                  {displayMediaTitle(welcome) || 'Bienvenida'}
                </h2>
                <button type="button" className="spiritual-hub-modal__close" onClick={closeWelcome} aria-label="Cerrar">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <SpiritualVideoEmbed media={welcome} title={displayMediaTitle(welcome) || 'Bienvenida'} />
              <button type="button" className="btn-secondary text-sm mt-3" onClick={closeWelcome}>
                Cerrar
              </button>
            </div>
          </div>,
          document.body,
        )
        : null}
    </div>
  );
}
